import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Microphone, Upload, Play, FileText, Image, Video, Waveform, 
  Sparkle, Eye, PaperPlaneTilt, Trash, X, CaretDown
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const formatOptions = [
  { value: "fullscreen_presenter", label: "Full-screen Presenter", icon: Video },
  { value: "screen_first", label: "Screen-first with Corner Presenter", icon: Play },
  { value: "slides_voice", label: "Slides + Voice", icon: FileText },
  { value: "avatar", label: "Avatar Lesson", icon: Sparkle },
];

const statusColors = {
  draft: "bg-zinc-500/20 text-zinc-400",
  generating: "bg-amber-500/20 text-amber-400",
  ready_for_review: "bg-blue-500/20 text-blue-400",
  approved: "bg-emerald-500/20 text-emerald-400",
  uploading: "bg-violet-500/20 text-violet-400",
  published: "bg-cyan-500/20 text-cyan-400",
};

const LessonCard = ({ lesson, onSelect, onDelete }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="bg-zinc-900/50 border border-white/5 rounded-2xl p-5 card-hover cursor-pointer"
    onClick={() => onSelect(lesson)}
  >
    <div className="flex items-start justify-between mb-3">
      <span className={`status-badge ${statusColors[lesson.status]}`}>
        {lesson.status.replace(/_/g, ' ')}
      </span>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(lesson.id); }}
        className="p-1.5 rounded-lg hover:bg-white/5 text-zinc-500 hover:text-rose-400 transition-colors"
      >
        <Trash size={16} />
      </button>
    </div>
    <h3 className="text-lg font-semibold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
      {lesson.title}
    </h3>
    <p className="text-sm text-zinc-500 mb-3">{lesson.chapter || 'No chapter'}</p>
    <div className="flex items-center gap-3 text-xs text-zinc-500">
      <span>{lesson.duration_minutes} min</span>
      <span>•</span>
      <span>{formatOptions.find(f => f.value === lesson.format)?.label || lesson.format}</span>
    </div>
  </motion.div>
);

export default function LessonStudio() {
  const [lessons, setLessons] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  // HeyGen state
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [avatars, setAvatars] = useState([]);
  const [voices, setVoices] = useState([]);
  const [selectedAvatar, setSelectedAvatar] = useState("");
  const [selectedVoice, setSelectedVoice] = useState("");
  const [loadingAvatars, setLoadingAvatars] = useState(false);

  // Form state
  const [title, setTitle] = useState("");
  const [chapter, setChapter] = useState("");
  const [module, setModule] = useState("");
  const [description, setDescription] = useState("");
  const [format, setFormat] = useState("fullscreen_presenter");
  const [duration, setDuration] = useState(5);
  const [voiceInstruction, setVoiceInstruction] = useState("");

  useEffect(() => {
    fetchLessons();
  }, []);

  const fetchLessons = async () => {
    try {
      const res = await axios.get(`${API}/lessons`);
      setLessons(res.data);
    } catch (err) {
      toast.error("Failed to fetch lessons");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!title) {
      toast.error("Please enter a lesson title");
      return;
    }

    try {
      const res = await axios.post(`${API}/lessons`, {
        title,
        chapter,
        module,
        description,
        format,
        duration_minutes: duration,
        voice_instruction: voiceInstruction,
      });
      setLessons([res.data, ...lessons]);
      toast.success("Lesson created!");
      resetForm();
      setShowCreate(false);
    } catch (err) {
      toast.error("Failed to create lesson");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/lessons/${id}`);
      setLessons(lessons.filter(l => l.id !== id));
      if (selectedLesson?.id === id) setSelectedLesson(null);
      toast.success("Lesson deleted");
    } catch (err) {
      toast.error("Failed to delete lesson");
    }
  };

  const handleGenerateScript = async () => {
    if (!selectedLesson) return;
    setGenerating(true);
    
    try {
      const res = await axios.post(`${API}/lessons/${selectedLesson.id}/generate-script`);
      setSelectedLesson({ ...selectedLesson, script: res.data.script, status: res.data.status });
      setLessons(lessons.map(l => l.id === selectedLesson.id ? { ...l, script: res.data.script, status: res.data.status } : l));
      toast.success("Script generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate script");
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateContent = async (contentType) => {
    if (!selectedLesson) return;
    setGenerating(true);
    
    try {
      const res = await axios.post(`${API}/lessons/${selectedLesson.id}/generate-content?content_type=${contentType}`);
      toast.success(`${contentType} generated!`);
      console.log(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to generate ${contentType}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateThumbnail = async () => {
    if (!selectedLesson) return;
    setGenerating(true);
    toast.info("Generating AI thumbnail... This takes 10-20 seconds.");
    
    try {
      const res = await axios.post(`${API}/lessons/${selectedLesson.id}/generate-thumbnail`);
      toast.success("Thumbnail generated and saved to Media Library!");
      setSelectedLesson({ 
        ...selectedLesson, 
        thumbnail_generated: true,
        thumbnail_storage_path: res.data.storage_path 
      });
      setLessons(lessons.map(l => l.id === selectedLesson.id ? { 
        ...l, 
        thumbnail_generated: true,
        thumbnail_storage_path: res.data.storage_path 
      } : l));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate thumbnail");
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateVoice = async () => {
    if (!selectedLesson) return;
    if (!selectedLesson.script) {
      toast.error("Generate a script first!");
      return;
    }
    setGenerating(true);
    
    try {
      const res = await axios.post(`${API}/lessons/${selectedLesson.id}/generate-voice`);
      toast.success("Voice narration generated with ElevenLabs!");
      console.log(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate voice");
    } finally {
      setGenerating(false);
    }
  };

  const openVideoDialog = async () => {
    if (!selectedLesson?.script) {
      toast.error("Generate a script first!");
      return;
    }
    
    setShowVideoDialog(true);
    setLoadingAvatars(true);
    
    try {
      const [avatarsRes, voicesRes] = await Promise.all([
        axios.get(`${API}/heygen/avatars`),
        axios.get(`${API}/heygen/voices`)
      ]);
      
      setAvatars(avatarsRes.data.avatars || []);
      setVoices(voicesRes.data.voices || []);
      
      // Set defaults
      if (avatarsRes.data.avatars?.length > 0) {
        setSelectedAvatar(avatarsRes.data.avatars[0].avatar_id);
      }
      if (voicesRes.data.voices?.length > 0) {
        setSelectedVoice(voicesRes.data.voices[0].voice_id);
      }
    } catch (err) {
      toast.error("Failed to load avatars and voices");
    } finally {
      setLoadingAvatars(false);
    }
  };

  const handleGenerateVideo = async () => {
    if (!selectedLesson || !selectedAvatar || !selectedVoice) {
      toast.error("Please select an avatar and voice");
      return;
    }
    
    setGenerating(true);
    setShowVideoDialog(false);
    
    try {
      const res = await axios.post(
        `${API}/lessons/${selectedLesson.id}/generate-video?avatar_id=${selectedAvatar}&voice_id=${selectedVoice}`
      );
      toast.success("Avatar video generation started! This takes 2-5 minutes.");
      setSelectedLesson({ ...selectedLesson, video_status: 'processing', heygen_video_id: res.data.video_id });
      setLessons(lessons.map(l => l.id === selectedLesson.id ? { ...l, video_status: 'processing' } : l));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate video");
    } finally {
      setGenerating(false);
    }
  };

  const handleCheckVideoStatus = async () => {
    if (!selectedLesson) return;
    
    try {
      const res = await axios.get(`${API}/lessons/${selectedLesson.id}/video-status`);
      const { status, video_url, thumbnail_url } = res.data;
      
      if (status === 'completed') {
        toast.success("Your avatar video is ready!");
        setSelectedLesson({ ...selectedLesson, video_status: status, video_url, thumbnail_url });
        setLessons(lessons.map(l => l.id === selectedLesson.id ? { ...l, video_status: status, video_url, thumbnail_url } : l));
      } else if (status === 'failed') {
        toast.error("Video generation failed: " + (res.data.error || "Unknown error"));
        setSelectedLesson({ ...selectedLesson, video_status: status });
      } else {
        toast.info(`Video status: ${status}. Check again in a minute.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to check video status");
    }
  };

  const handleUpdateStatus = async (status) => {
    if (!selectedLesson) return;
    
    try {
      await axios.put(`${API}/lessons/${selectedLesson.id}`, { status });
      setSelectedLesson({ ...selectedLesson, status });
      setLessons(lessons.map(l => l.id === selectedLesson.id ? { ...l, status } : l));
      toast.success("Status updated!");
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const resetForm = () => {
    setTitle("");
    setChapter("");
    setModule("");
    setDescription("");
    setFormat("fullscreen_presenter");
    setDuration(5);
    setVoiceInstruction("");
  };

  return (
    <div data-testid="lesson-studio">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Lesson Studio
          </h1>
          <p className="text-zinc-400">Create and manage your academy lessons</p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          data-testid="create-lesson-btn"
          className="h-12 px-6 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold"
        >
          <Plus size={20} className="mr-2" />
          New Lesson
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lessons List */}
        <div className="lg:col-span-2">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : lessons.length === 0 ? (
            <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-12 text-center">
              <Video size={48} className="mx-auto mb-4 text-zinc-600" />
              <h3 className="text-xl font-semibold text-white mb-2">No lessons yet</h3>
              <p className="text-zinc-400 mb-6">Create your first lesson to get started</p>
              <Button
                onClick={() => setShowCreate(true)}
                className="h-12 px-6 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold"
              >
                Create Lesson
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {lessons.map((lesson) => (
                <LessonCard
                  key={lesson.id}
                  lesson={lesson}
                  onSelect={setSelectedLesson}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>

        {/* Lesson Details / Editor */}
        <div className="lg:col-span-1">
          {selectedLesson ? (
            <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6 sticky top-8">
              <div className="flex items-center justify-between mb-4">
                <span className={`status-badge ${statusColors[selectedLesson.status]}`}>
                  {selectedLesson.status.replace(/_/g, ' ')}
                </span>
                <button
                  onClick={() => setSelectedLesson(null)}
                  className="p-1.5 rounded-lg hover:bg-white/5 text-zinc-400"
                >
                  <X size={18} />
                </button>
              </div>
              
              <h3 className="text-xl font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {selectedLesson.title}
              </h3>

              {/* AI Generation Buttons */}
              <div className="space-y-3 mb-6">
                <Button
                  onClick={handleGenerateScript}
                  disabled={generating}
                  data-testid="generate-script-btn"
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-semibold"
                >
                  {generating ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Generating...
                    </span>
                  ) : (
                    <>
                      <Sparkle size={20} className="mr-2" weight="fill" />
                      Generate Script
                    </>
                  )}
                </Button>

                <div className="grid grid-cols-2 gap-2">
                  <Button
                    onClick={() => handleGenerateContent('caption')}
                    disabled={generating}
                    className="h-10 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white text-sm"
                  >
                    Caption
                  </Button>
                  <Button
                    onClick={() => handleGenerateContent('summary')}
                    disabled={generating}
                    className="h-10 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white text-sm"
                  >
                    Summary
                  </Button>
                  <Button
                    onClick={() => handleGenerateContent('transcript')}
                    disabled={generating}
                    className="h-10 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white text-sm"
                  >
                    Transcript
                  </Button>
                  <Button
                    onClick={handleGenerateThumbnail}
                    disabled={generating}
                    data-testid="generate-thumbnail-btn"
                    className="h-10 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white text-sm font-medium"
                  >
                    <Image size={16} className="mr-1" />
                    AI Thumbnail
                  </Button>
                </div>

                {/* Thumbnail Preview */}
                {selectedLesson.thumbnail_storage_path && (
                  <div className="rounded-xl overflow-hidden bg-zinc-950 border border-white/10">
                    <p className="text-xs text-zinc-500 px-3 py-2 border-b border-white/5">Generated Thumbnail</p>
                    <img
                      src={`${API}/lessons/${selectedLesson.id}/thumbnail?auth=${localStorage.getItem('token')}`}
                      alt="Lesson Thumbnail"
                      className="w-full h-auto"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}

                {/* Voice Generation Button */}
                <Button
                  onClick={handleGenerateVoice}
                  disabled={generating || !selectedLesson.script}
                  data-testid="generate-voice-btn"
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 text-black font-semibold"
                >
                  {generating ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Generating Voice...
                    </span>
                  ) : (
                    <>
                      <Waveform size={20} className="mr-2" weight="fill" />
                      Generate Voice (ElevenLabs)
                    </>
                  )}
                </Button>

                {/* HeyGen Avatar Video Button */}
                <Button
                  onClick={openVideoDialog}
                  disabled={generating || !selectedLesson.script}
                  data-testid="generate-video-btn"
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-violet-500 to-violet-600 hover:from-violet-400 hover:to-violet-500 text-white font-semibold"
                >
                  {generating ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Generating Video...
                    </span>
                  ) : (
                    <>
                      <Video size={20} className="mr-2" weight="fill" />
                      Generate Avatar Video (HeyGen)
                    </>
                  )}
                </Button>

                {/* Video Status */}
                {selectedLesson.video_status && (
                  <div className={`p-3 rounded-xl text-sm ${
                    selectedLesson.video_status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                    selectedLesson.video_status === 'failed' ? 'bg-rose-500/20 text-rose-400' :
                    'bg-amber-500/20 text-amber-400'
                  }`}>
                    Video Status: {selectedLesson.video_status}
                    {selectedLesson.video_status === 'processing' && (
                      <Button
                        onClick={handleCheckVideoStatus}
                        size="sm"
                        className="ml-2 h-7 px-3 bg-white/10 hover:bg-white/20 text-white"
                      >
                        Check Status
                      </Button>
                    )}
                  </div>
                )}

                {/* Video Preview */}
                {selectedLesson.video_url && (
                  <div className="rounded-xl overflow-hidden bg-zinc-950">
                    <video
                      src={selectedLesson.video_url}
                      controls
                      className="w-full"
                      poster={selectedLesson.thumbnail_url}
                    />
                  </div>
                )}
              </div>

              {/* Script Preview */}
              {selectedLesson.script && (
                <div className="mb-6">
                  <Label className="text-zinc-400 mb-2">Generated Script</Label>
                  <div className="max-h-48 overflow-y-auto p-4 bg-zinc-950 rounded-xl text-sm text-zinc-300 whitespace-pre-wrap">
                    {selectedLesson.script}
                  </div>
                </div>
              )}

              {/* Status Actions */}
              <div className="space-y-3">
                <Label className="text-zinc-400">Update Status</Label>
                <Select value={selectedLesson.status} onValueChange={handleUpdateStatus}>
                  <SelectTrigger className="h-12 bg-zinc-900 border-white/10 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="ready_for_review">Ready for Review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>

                <Button
                  onClick={() => handleUpdateStatus('published')}
                  data-testid="send-to-academy-btn"
                  className="w-full h-12 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
                >
                  <PaperPlaneTilt size={20} className="mr-2" />
                  Send to Academy
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-8 text-center">
              <Eye size={32} className="mx-auto mb-3 text-zinc-600" />
              <p className="text-zinc-400">Select a lesson to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Lesson Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Create New Lesson
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Lesson Title *</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Introduction to Digital Marketplaces"
                data-testid="lesson-title-input"
                className="h-12 bg-zinc-950 border-white/10 rounded-xl"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Chapter</Label>
                <Input
                  value={chapter}
                  onChange={(e) => setChapter(e.target.value)}
                  placeholder="e.g., Chapter 1"
                  className="h-12 bg-zinc-950 border-white/10 rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Module</Label>
                <Input
                  value={module}
                  onChange={(e) => setModule(e.target.value)}
                  placeholder="e.g., Getting Started"
                  className="h-12 bg-zinc-950 border-white/10 rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400">Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of the lesson..."
                className="bg-zinc-950 border-white/10 rounded-xl min-h-[100px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Lesson Format</Label>
                <Select value={format} onValueChange={setFormat}>
                  <SelectTrigger className="h-12 bg-zinc-950 border-white/10 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {formatOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Duration (minutes)</Label>
                <Input
                  type="number"
                  value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value) || 5)}
                  min={1}
                  max={60}
                  className="h-12 bg-zinc-950 border-white/10 rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400">Voice Instruction (Optional)</Label>
              <Textarea
                value={voiceInstruction}
                onChange={(e) => setVoiceInstruction(e.target.value)}
                placeholder="Describe what you want the lesson to cover..."
                className="bg-zinc-950 border-white/10 rounded-xl min-h-[80px]"
              />
            </div>

            {/* Upload Zone - Real file upload */}
            <div className="space-y-2">
              <Label className="text-zinc-400">Media Files (Optional)</Label>
              <div className="upload-zone p-6 text-center relative">
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*,audio/*,.pdf,.ppt,.pptx"
                  onChange={async (e) => {
                    if (e.target.files?.length) {
                      const files = Array.from(e.target.files);
                      toast.info(`Uploading ${files.length} file(s)...`);
                      
                      for (const file of files) {
                        try {
                          const formData = new FormData();
                          formData.append('file', file);
                          formData.append('folder', 'videos');
                          formData.append('tags', title || 'lesson');
                          
                          await axios.post(`${API}/media/upload`, formData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                        } catch (err) {
                          toast.error(`Failed to upload ${file.name}`);
                        }
                      }
                      toast.success(`${files.length} file(s) uploaded to Media Library!`);
                    }
                  }}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                />
                <Upload size={32} className="mx-auto mb-3 text-zinc-500" />
                <p className="text-amber-500 font-medium mb-1">Tap to upload files</p>
                <p className="text-xs text-zinc-500">Photos, videos, slides, screen recordings (Max 100MB)</p>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => setShowCreate(false)}
                className="flex-1 h-12 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                data-testid="save-lesson-btn"
                className="flex-1 h-12 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold"
              >
                Create Lesson
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* HeyGen Video Generation Dialog */}
      <Dialog open={showVideoDialog} onOpenChange={setShowVideoDialog}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Generate Avatar Video
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 mt-4">
            {loadingAvatars ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Select Avatar</Label>
                  <Select value={selectedAvatar} onValueChange={setSelectedAvatar}>
                    <SelectTrigger className="h-12 bg-zinc-950 border-white/10 rounded-xl">
                      <SelectValue placeholder="Choose an avatar" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10 max-h-60">
                      {avatars.map(avatar => (
                        <SelectItem key={avatar.avatar_id} value={avatar.avatar_id}>
                          <div className="flex items-center gap-2">
                            {avatar.preview && (
                              <img src={avatar.preview} alt="" className="w-8 h-8 rounded-full object-cover" />
                            )}
                            <span>{avatar.name || avatar.avatar_id}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-400">Select Voice</Label>
                  <Select value={selectedVoice} onValueChange={setSelectedVoice}>
                    <SelectTrigger className="h-12 bg-zinc-950 border-white/10 rounded-xl">
                      <SelectValue placeholder="Choose a voice" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10 max-h-60">
                      {voices.map(voice => (
                        <SelectItem key={voice.voice_id} value={voice.voice_id}>
                          {voice.name} ({voice.language})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="p-4 bg-zinc-800/50 rounded-xl">
                  <p className="text-sm text-zinc-400 mb-2">Script Preview</p>
                  <p className="text-sm text-white line-clamp-3">
                    {selectedLesson?.script?.substring(0, 200)}...
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    onClick={() => setShowVideoDialog(false)}
                    className="flex-1 h-12 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleGenerateVideo}
                    disabled={!selectedAvatar || !selectedVoice}
                    className="flex-1 h-12 rounded-xl bg-gradient-to-r from-violet-500 to-violet-600 hover:from-violet-400 hover:to-violet-500 text-white font-semibold"
                  >
                    <Video size={20} className="mr-2" />
                    Generate Video
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
